import MuiIconButton, { IconButtonProps } from '@mui/material/IconButton'
import Tooltip, { TooltipProps } from '@mui/material/Tooltip'
import cn from 'classnames'
import styles from './IconButton.module.scss'

interface IProps extends Omit<IconButtonProps, 'title'> {
  /**
   * Tooltip text, and the accessible name unless the caller sets its own
   * `aria-label`. Required: the only child is a glyph, so without this the
   * button has no name at all.
   */
  title: string
  /** Set when a clickable ancestor already owns the tooltip. */
  disableTooltip?: boolean
  /** Matches MUI's own default, which is where all but three of these sat. */
  tooltipPlacement?: TooltipProps['placement']
  tooltipArrow?: boolean
  /**
   * Fixed Atlas box. `auto` keeps MUI's content sizing, so a call site that
   * sizes itself keeps the geometry it has.
   */
  box?: 'auto' | 'sm' | 'xs'
}

/**
 * NEUTRAL (transparent, neutral-dark) utility icon button — see the button
 * color convention in Common/Button/Button.tsx. Use for utility actions
 * (sort, export, expand, chart toggle), not for affirmative/add actions.
 */
const IconButton = ({
  title,
  disableTooltip,
  tooltipPlacement = 'bottom',
  tooltipArrow,
  box = 'auto',
  className,
  classes,
  ...props
}: IProps) => {
  const button = (
    <MuiIconButton
      classes={{ root: styles.root, ...classes }}
      // Before the spread: a caller whose name differs from its tooltip keeps its own.
      // `|| undefined` because an empty `aria-label` strips the name outright —
      // worse than none — and a conditional title can legitimately be empty.
      aria-label={title || undefined}
      {...props}
      className={cn(styles.controlRadius, className, {
        [styles.boxSm]: box === 'sm',
        [styles.boxXs]: box === 'xs',
      })}
    />
  )

  if (disableTooltip) {
    return button
  }

  return (
    <Tooltip title={title} placement={tooltipPlacement} arrow={tooltipArrow}>
      {/* A disabled MUI button emits no pointer events, so the tooltip needs a live anchor. */}
      {props.disabled ? <span className={styles.tooltipAnchor}>{button}</span> : button}
    </Tooltip>
  )
}

export default IconButton
